import * as React from "react";
import { Box } from "../../components/ui/box.tsx";
import { Input } from "../../components/ui/input.tsx";
import {
  hexToHslToken,
  hslTokenToHex,
  normalizeHexColor,
} from "../../lib/color-convert.ts";
import type { Omittable } from "../../lib/props.ts";
import { clamp } from "../../lib/utils.ts";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type HsvaColor = { h: number; s: number; v: number; a: number };

export type ColorPickerProps = Omittable<{
  /** HSL token (`"248 76% 60%"`) by default, or hex with `format="hex"`. */
  value: string;
  /** Called continuously as the user drags or types a valid hex value. */
  onChange: (value: string) => void;
  /** Called when a drag, keyboard adjustment, or hex edit is complete. */
  onChangeComplete?: (value: string) => void;
  /** Storage and callback format. Defaults to the existing HSL token format. */
  format?: "hsl" | "hex";
  /** Show an editable hex field. Available with `format="hex"`. */
  showInput?: boolean;
  /** Width/height of the saturation area in pixels. Defaults to 200. */
  size?: number;
  /**
   * Height of the saturation area, for hosts where the classic square reads
   * as a wall of color (a wide popover editor). Defaults to `size`.
   */
  height?: number;
}>;

// ---------------------------------------------------------------------------
// Color math
// ---------------------------------------------------------------------------


function parseHsl(hsl: string): { h: number; s: number; l: number } {
  const parts = hsl.replace(/%/g, "").split(/\s+/).map(Number);
  return { h: parts[0] || 0, s: parts[1] || 0, l: parts[2] || 0 };
}

function formatHsl(h: number, s: number, l: number): string {
  return `${Math.round(h)} ${Math.round(s)}% ${Math.round(l)}%`;
}

function parseColor(value: string, format: "hsl" | "hex") {
  return parseHsl(
    format === "hex" ? (hexToHslToken(value) ?? "0 0% 0%") : value,
  );
}

function hslaToHsva(h: number, s: number, l: number): HsvaColor {
  s /= 100;
  l /= 100;
  const v = l + s * Math.min(l, 1 - l);
  const sv = v === 0 ? 0 : 2 * (1 - l / v);
  return { h, s: sv * 100, v: v * 100, a: 1 };
}

function hsvaToHsla(hsva: HsvaColor): { h: number; s: number; l: number } {
  const { h, s, v } = hsva;
  const hh = ((200 - s) * v) / 100;
  return {
    h: Math.round(h),
    s: Math.round(
      hh > 0 && hh < 200
        ? ((s * v) / 100 / (hh <= 100 ? hh : 200 - hh)) * 100
        : 0,
    ),
    l: Math.round(hh / 2),
  };
}

function hsvaToHslString(hsva: HsvaColor): string {
  const { h, s, l } = hsvaToHsla(hsva);
  return `hsl(${h}, ${Math.round(s)}%, ${Math.round(l)}%)`;
}

// ---------------------------------------------------------------------------
// Interaction hook — pointer drag on a rectangular area, with pointer capture.
// Reports normalized { left: 0..1, top: 0..1 }.
// ---------------------------------------------------------------------------

function useInteraction(
  onMove: (pos: { left: number; top: number }) => void,
  onDragStart?: () => void,
  onDragEnd?: () => void,
) {
  const containerRef = React.useRef<HTMLDivElement>(null);

  const getPosition = React.useCallback(
    (e: PointerEvent) => {
      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect) return;
      onMove({
        left: clamp((e.clientX - rect.left) / rect.width, 0, 1),
        top: clamp((e.clientY - rect.top) / rect.height, 0, 1),
      });
    },
    [onMove],
  );

  const onPointerDown = React.useCallback(
    (e: React.PointerEvent) => {
      e.preventDefault();
      const el = containerRef.current;
      if (!el) return;
      el.setPointerCapture(e.pointerId);
      onDragStart?.();
      getPosition(e.nativeEvent);
    },
    [getPosition, onDragStart],
  );

  const onPointerMove = React.useCallback(
    (e: React.PointerEvent) => {
      if (e.buttons > 0) {
        getPosition(e.nativeEvent);
      }
    },
    [getPosition],
  );

  const onPointerEnd = React.useCallback(() => {
    onDragEnd?.();
  }, [onDragEnd]);

  return { containerRef, onPointerDown, onPointerMove, onPointerEnd };
}

// ---------------------------------------------------------------------------
// Pointer (the draggable circle)
// ---------------------------------------------------------------------------

const POINTER_SIZE = 20;

function Pointer({
  left,
  top,
  color,
}: {
  left: number;
  top: number;
  color: string;
}) {
  return (
    <Box
      data-slot="color-picker-pointer"
      position="absolute"
      rounded="full"
      width={POINTER_SIZE}
      height={POINTER_SIZE}
      // Dynamic drag position and live swatch color — style is the only channel
      // for computed values; the static ring/shadow ride along.
      style={{
        left: `${left * 100}%`,
        top: `${top * 100}%`,
        transform: "translate(-50%, -50%)",
        backgroundColor: color,
        border: "2px solid #fff",
        boxShadow: "0 1px 4px rgba(0,0,0,0.3)",
        pointerEvents: "none",
      }}
    />
  );
}

// ---------------------------------------------------------------------------
// Saturation area — 2D picker for saturation (X) and value/brightness (Y)
// ---------------------------------------------------------------------------

function SaturationArea({
  hsva,
  size,
  height,
  onChange,
  onDragStart,
  onDragEnd,
}: Omittable<{
  hsva: HsvaColor;
  size: number;
  height: number;
  onChange: (partial: Partial<HsvaColor>) => void;
  onDragStart?: () => void;
  onDragEnd?: () => void;
}>) {
  const onMove = React.useCallback(
    (pos: { left: number; top: number }) => {
      onChange({ s: pos.left * 100, v: (1 - pos.top) * 100 });
    },
    [onChange],
  );

  const { containerRef, onPointerDown, onPointerMove, onPointerEnd } =
    useInteraction(onMove, onDragStart, onDragEnd);

  const left = hsva.s / 100;
  const top = 1 - hsva.v / 100;
  const displayColor = hsvaToHslString(hsva);
  const handleKeyDown = (event: React.KeyboardEvent) => {
    const step = event.shiftKey ? 10 : 2;
    if (event.key === "ArrowLeft") onChange({ s: clamp(hsva.s - step, 0, 100) });
    else if (event.key === "ArrowRight")
      onChange({ s: clamp(hsva.s + step, 0, 100) });
    else if (event.key === "ArrowDown")
      onChange({ v: clamp(hsva.v - step, 0, 100) });
    else if (event.key === "ArrowUp")
      onChange({ v: clamp(hsva.v + step, 0, 100) });
    else return;
    event.preventDefault();
  };

  return (
    <Box
      ref={containerRef}
      data-slot="color-picker-saturation"
      position="relative"
      rounded="md"
      cursor="crosshair"
      width={size}
      height={height}
      role="slider"
      tabIndex={0}
      aria-label="Saturation and brightness"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(hsva.s)}
      aria-valuetext={`${Math.round(hsva.s)}% saturation, ${Math.round(hsva.v)}% brightness`}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerEnd}
      onPointerCancel={onPointerEnd}
      onKeyDown={handleKeyDown}
      onKeyUp={onPointerEnd}
      style={{
        backgroundImage:
          "linear-gradient(to top, #000, transparent), linear-gradient(to right, #fff, transparent)",
        backgroundColor: `hsl(${hsva.h}, 100%, 50%)`,
        touchAction: "none",
      }}
    >
      <Pointer left={left} top={top} color={displayColor} />
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Hue slider — horizontal rainbow strip
// ---------------------------------------------------------------------------

const HUE_HEIGHT = 16;

function HueSlider({
  hue,
  width,
  onChange,
  onDragStart,
  onDragEnd,
}: Omittable<{
  hue: number;
  width: number;
  onChange: (partial: Partial<HsvaColor>) => void;
  onDragStart?: () => void;
  onDragEnd?: () => void;
}>) {
  const onMove = React.useCallback(
    (pos: { left: number }) => {
      onChange({ h: pos.left * 360 });
    },
    [onChange],
  );

  const { containerRef, onPointerDown, onPointerMove, onPointerEnd } =
    useInteraction(onMove, onDragStart, onDragEnd);
  const left = hue / 360;
  const displayColor = `hsl(${Math.round(hue)}, 100%, 50%)`;
  const handleKeyDown = (event: React.KeyboardEvent) => {
    const step = event.shiftKey ? 10 : 2;
    if (event.key === "ArrowLeft" || event.key === "ArrowDown") {
      onChange({ h: (hue - step + 360) % 360 });
    } else if (event.key === "ArrowRight" || event.key === "ArrowUp") {
      onChange({ h: (hue + step) % 360 });
    } else if (event.key === "Home") onChange({ h: 0 });
    else if (event.key === "End") onChange({ h: 359 });
    else return;
    event.preventDefault();
  };

  return (
    <Box
      ref={containerRef}
      data-slot="color-picker-hue"
      position="relative"
      rounded="full"
      cursor="crosshair"
      width={width}
      height={HUE_HEIGHT}
      role="slider"
      tabIndex={0}
      aria-label="Hue"
      aria-valuemin={0}
      aria-valuemax={359}
      aria-valuenow={Math.round(hue)}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerEnd}
      onPointerCancel={onPointerEnd}
      onKeyDown={handleKeyDown}
      onKeyUp={onPointerEnd}
      style={{
        background:
          "linear-gradient(to right, #f00 0%, #ff0 17%, #0f0 33%, #0ff 50%, #00f 67%, #f0f 83%, #f00 100%)",
        touchAction: "none",
      }}
    >
      <Pointer left={left} top={0.5} color={displayColor} />
    </Box>
  );
}

// ---------------------------------------------------------------------------
// ColorPicker
// ---------------------------------------------------------------------------

const HEX_DRAFT = /^#?(?:[\da-f]{3}|[\da-f]{6})$/i;

/**
 * Parse what the field currently holds. The `#` is optional — a typist reaches
 * for the digits, not the hash — and the three-digit shorthand is honoured on
 * commit only: live-expanding `#111` rewrites a value that is still a prefix
 * of `#1119ff` out from under the caret.
 */
function parseHexDraft(draft: string, shorthand: boolean): string | null {
  const candidate = draft.trim();
  if (!HEX_DRAFT.test(candidate)) return null;
  const digits = candidate.replace("#", "");
  if (digits.length === 3 && !shorthand) return null;
  return normalizeHexColor(`#${digits}`);
}

function HexInput({
  value,
  onChange,
  onCommit,
}: {
  value: string;
  onChange: (value: string) => void;
  onCommit: (value: string) => void;
}) {
  const [draft, setDraft] = React.useState(value);
  // Escape abandons the edit, and the blur it forces must not re-commit the
  // stale draft the commit-on-blur handler still closes over.
  const abandoned = React.useRef(false);
  // A live keystroke round-trips through the host and returns as a new
  // `value`; while the field has focus that echo must not overwrite the draft,
  // or the field rewrites itself between characters.
  const editing = React.useRef(false);

  React.useEffect(() => {
    if (editing.current) return;
    setDraft(value);
  }, [value]);

  // Commit the draft: normalize a valid one, revert an invalid one to the
  // last accepted value. Live (per-keystroke) updates go through the inline
  // onChange handler below, never through here.
  const applyDraft = () => {
    const normalized = parseHexDraft(draft, true);
    if (!normalized) {
      setDraft(value);
      return;
    }
    setDraft(normalized);
    onChange(normalized);
    onCommit(normalized);
  };

  return (
    <Input
      aria-label="Hex color"
      size="sm"
      value={draft}
      onFocus={() => {
        editing.current = true;
      }}
      onBlur={() => {
        editing.current = false;
        if (abandoned.current) {
          abandoned.current = false;
          return;
        }
        applyDraft();
      }}
      onChange={(event) => {
        const next = event.target.value;
        setDraft(next);
        const normalized = parseHexDraft(next, false);
        if (normalized) onChange(normalized);
      }}
      onKeyDown={(event) => {
        if (event.key === "Enter") {
          applyDraft();
          event.currentTarget.blur();
        }
        if (event.key === "Escape") {
          abandoned.current = true;
          editing.current = false;
          setDraft(value);
          event.currentTarget.blur();
        }
      }}
    />
  );
}

/**
 * Inline free-color picker: a saturation/value area over a hue slider.
 * Accepts and emits HSL strings in theme-token format ("H S% L%") — the
 * caller owns any conversion to a paintable color.
 */
export function ColorPicker({
  value,
  onChange,
  onChangeComplete,
  format = "hsl",
  showInput = false,
  size = 200,
  height,
}: ColorPickerProps) {
  const parsed = React.useMemo(() => parseColor(value, format), [format, value]);
  const [hsva, setHsva] = React.useState<HsvaColor>(() =>
    hslaToHsva(parsed.h, parsed.s, parsed.l),
  );
  // The gesture-time source of truth: pointer moves land faster than renders,
  // so a merge over the STATE would drop the moves since the last commit.
  const hsvaRef = React.useRef(hsva);
  const isDragging = React.useRef(false);

  // Sync from external value changes — skip while dragging to avoid a lossy
  // HSL↔HSV round-trip overwriting precise internal state mid-gesture.
  React.useEffect(() => {
    if (isDragging.current) return;
    const next = hslaToHsva(parsed.h, parsed.s, parsed.l);
    const prev = hsvaRef.current;
    if (next.h === prev.h && next.s === prev.s && next.v === prev.v) return;
    hsvaRef.current = next;
    setHsva(next);
  }, [parsed.h, parsed.s, parsed.l]);

  const onChangeRef = React.useRef(onChange);
  onChangeRef.current = onChange;
  const onChangeCompleteRef = React.useRef(onChangeComplete);
  onChangeCompleteRef.current = onChangeComplete;
  const lastValueRef = React.useRef(value);

  const outputValue = React.useCallback(
    (color: HsvaColor) => {
      const { h, s, l } = hsvaToHsla(color);
      const hsl = formatHsl(h, s, l);
      return format === "hex" ? (hslTokenToHex(hsl) ?? "#000000") : hsl;
    },
    [format],
  );

  // Apply a USER gesture: state and emission in one imperative step. onChange
  // fires only from here — never from an effect watching state — so nothing a
  // render does (mount, a StrictMode replay, an external value sync) can
  // masquerade as a pick. A host keying "the user chose a color" off onChange
  // depends on that.
  const applyHsva = React.useCallback(
    (next: HsvaColor) => {
      hsvaRef.current = next;
      setHsva(next);
      const output = outputValue(next);
      lastValueRef.current = output;
      onChangeRef.current(output);
    },
    [outputValue],
  );

  const handleChange = React.useCallback(
    (partial: Partial<HsvaColor>) => {
      applyHsva({ ...hsvaRef.current, ...partial });
    },
    [applyHsva],
  );

  const handleDragStart = React.useCallback(() => {
    isDragging.current = true;
  }, []);

  const handleDragEnd = React.useCallback(() => {
    isDragging.current = false;
    onChangeCompleteRef.current?.(lastValueRef.current);
  }, []);

  const handleInputChange = React.useCallback(
    (next: string) => {
      const parsedNext = parseColor(next, "hex");
      applyHsva(hslaToHsva(parsedNext.h, parsedNext.s, parsedNext.l));
    },
    [applyHsva],
  );

  return (
    <Box
      userSelect="none"
      data-slot="color-picker"
      direction="column"
      gap="md"
    >
      <SaturationArea
        hsva={hsva}
        size={size}
        height={height ?? size}
        onChange={handleChange}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      />
      <HueSlider
        hue={hsva.h}
        width={size}
        onChange={handleChange}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      />
      {format === "hex" && showInput ? (
        <Box width={size}>
          <HexInput
            value={normalizeHexColor(value) ?? lastValueRef.current}
            onChange={handleInputChange}
            onCommit={(next) => onChangeCompleteRef.current?.(next)}
          />
        </Box>
      ) : null}
    </Box>
  );
}
